import React, { useEffect } from "react";
import { HashRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import RouteLogger from "./components/RouteLogger";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./user/Home";
import RegistrationPage from "./user/RegistrationPage";
import LoginPage from "./user/LoginPage";

import ServicesPage from "./user/Post";

import CustomerManagementPage from "./admin/CustomerManagementPage";
import Contacts from "./admin/Contacts";
import Contact from "./admin/Contact";
import Level from "./admin/Level";
import Products from "./admin/Products";
import Categories from "./admin/Categories";
import Providers from "./admin/Providers";
import Provider from "./admin/Provider";

import PostDetail from "./user/PostDetail";
import AddOrUpdatePost from "./admin/AddOrUpdatePost.jsx";
import OrderTable from "./user/OrderTable";
import TthOrderTable from "./user/TthOrderTable";
import UserProducts from "./user/UserProducts";
import UserInvoices from "./user/UserInvoices";

import OrderAdmin from "./admin/OrderAdmin";
import TthOrderAdmin from "./admin/TthOrderAdmin";
import TthReconciliationManagement from "./admin/TthReconciliationManagement";

import ModelDetailComponent from "./admin/ModelDetailComponent";
import AddOrder from "./admin/AddOrder";
import EditOrder from "./admin/EditOrder";
import PrivateRoute from "./PrivateRoute";
import ChildFeatureRoute from "./components/ChildFeatureRoute";
import { CHILD_FEATURES } from "./libs/childUserPermission";
import PostsPage from "./admin/PostsPage";
import BannerManagement from "./admin/InitialBanners";
import Order from "./admin/Order";
import AddOrderUser from "./user/AddOrderUser";
import EditOrderUser from "./user/EditOrderUser";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "./redux/userSlice";
import ReconciliationManagement from "./admin/ReconciliationManagement";
import NormalInvoices from "./admin/NormalInvoices";
import Profile from "./user/Profile";
import ChildUsersPage from "./user/ChildUsersPage";
import UpdateUser from "./user/UpdateUser.jsx";

import UpdateCustomerCode from "./admin/Customer.jsx";
import AddCustomerCode from "./admin/CustommerAdd.jsx";
import {
  CUSTOMER_LIST_PATH,
  CUSTOMER_NEW_PATH,
} from "./routes/customerRoutes";
import {
  LegacyCustomerAddCodeRedirect,
  LegacyCustomerDetailRedirect,
} from "./components/LegacyCustomerRouteRedirects";
import ContactForm from "./user/ContactForm.jsx";
import ContactExploration from "./admin/ContactExploration.jsx";
import Policy from "./user/Policy.jsx";
import PolicyDetail from "./user/PolicyDetail.jsx";
import OrderSearch from "./user/OrderSearch.jsx";
import ExcelDataPage from "./admin/ExcelDataPage.jsx";
import ParameterPage from "./admin/ParameterPage";
import OrderParameter from "./admin/OrderParameter";
import ParameterUser from "./admin/ParameterUser";
import Databatch from "./admin/Databatch";
import JobLogs from "./admin/JobLogs";

import InvoiceManagement from "./admin/InvoiceManagement";
import Invoices from "./admin/Invoices";
import ContactOrders from "./admin/ContactOrders";
import ContactInvoices from "./admin/ContactInvoices";
import ContactVatOrders from "./admin/ContactVatOrders";
import ContactVatInvoices from "./admin/ContactVatInvoices";

import AdminDashboard from "./admin/AdminDashboard";
import ListCustomers from "./admin/ListCustomers";
import About from "./admin/About";
import AboutUs from "./pages/AboutUs.jsx";
import News from "./pages/News/News.jsx";
import MarketNews from "./pages/News/MarketNews.jsx";
import KnowledgeNews from "./pages/News/KnowledgeNews.jsx";
import SourceNews from "./pages/News/SourceNews.jsx";
import NewsDetail from "./pages/News/NewsDetail.jsx";
import UserReconciliation from "./user/UserReconciliation";
import UserExcelPage from "./user/UserExcelPage";
import Instruction from "./pages/Instruction.jsx";
import NewsList from "./admin/NewsList.jsx";
import NewsEditor from "./admin/NewsEditor.jsx";
import OrderProcessWarningConfig from "./admin/OrderProcessWarningConfig.jsx";
import FloatingContactWidget from "./components/FloatingContactWidget.jsx";
import TradeWebChatClient from "./components/TradeWebChatClient.jsx";
import AdminChatInbox from "./admin/AdminChatInbox.jsx";
import AdminOmnichannel from "./admin/AdminOmnichannel.jsx";
import AdminMyProfile from "./admin/AdminMyProfile.jsx";
import AdminSystemSettings from "./admin/AdminSystemSettings.jsx";
import EnterprisePermissionPage from "./admin/EnterprisePermissionPage.jsx";
import UsersAdminPage from "./admin/UsersAdminPage.jsx";
import AdminWelcome from "./admin/AdminWelcome.jsx";
import ControlPanel from "./admin/ControlPanel.jsx";

import ReportManagement from "./admin/accounting/ReportManagement.jsx";
import AccountingConfig from "./admin/accounting/AccountingConfig.jsx";
import AccountingFeatureGate from "./components/AccountingFeatureGate.jsx";
import DebtManagement from "./admin/accounting/DebtManagement.jsx";
import VoucherManagement from "./admin/accounting/VoucherManagement.jsx";
import HrAccounting from "./admin/accounting/HrAccounting.jsx";
import ScanInPage from "./admin/warehouse/ScanInPage.jsx";
import ScanOutPage from "./admin/warehouse/ScanOutPage.jsx";
import UnknownCodesPage from "./admin/warehouse/UnknownCodesPage.jsx";
import {
  fetchWarehousePermissions,
  WAREHOUSE_SCAN_IN_FEATURE,
  WAREHOUSE_SCAN_OUT_FEATURE,
  WAREHOUSE_UNCLAIMED_FEATURE,
} from "./libs/customerCreatePermission";

const App = () => {
  useEffect(() => {
    const { pathname, search, hash } = window.location;
    if (!hash && pathname && pathname !== '/') {
      window.location.replace(`${window.location.origin}/#${pathname}${search}`);
    }
  }, []);

  useEffect(() => {
    const handleContextMenu = (event) => {
      //event.preventDefault(); // Ngừng hiển thị menu chuột phải
    };

    const handleKeyDown = (event) => {
      if (event.key === "F12") {
        //event.preventDefault(); // Ngừng hiển thị Developer Tools khi nhấn F12
      }
    };

    // Thêm event listener khi component mount
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup khi component unmount
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []); // Empty dependency array để chỉ chạy 1 lần khi component mount

  const user = useSelector((state) => state.user.user) || {};
  const dispatch = useDispatch();
  const userRole = String(user?.role || "").toLowerCase();
  const isStaffUser =
    user?.isAdmin === true ||
    user?.admin === true ||
    user?.isMember === true ||
    user?.member === true ||
    userRole === "admin" ||
    userRole === "administrator" ||
    userRole === "member";

  useEffect(() => {
    const storedUser = localStorage.getItem("persist:root"); // Lấy persisted state từ localStorage
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      const user =
        parsedUser.user && parsedUser.user !== "null"
          ? JSON.parse(parsedUser.user)
          : null;
      if (user) {
        dispatch(setUser(user)); // Nếu có user, dispatch action setUser vào Redux store
      }
    }
  }, [dispatch]);

  return (
    <Router>
      <ScrollToTop />
      <RouteLogger />
      <Routes>
        {user?.isAdmin === true || user?.isMember === true ? (
          <Route
            path="/"
            element={
              <PrivateRoute roles={["admin"]}>
                <CustomerManagementPage />
              </PrivateRoute>
            }
          />
        ) : user.role === "user" ? (
          <Route
            path="/"
            element={
              <PrivateRoute roles={["user"]}>
                <Home />
              </PrivateRoute>
            }
          />
        ) : (
          <Route path="/" element={<Home />} />
        )}
        <Route path="/posts/:id/:link" element={<PostDetail />} />
        <Route
          path="/profile"
          element={
            <PrivateRoute roles={["user"]}>
              <Profile />
            </PrivateRoute>
          }
        />
        <Route
          path="/danh-sach-users"
          element={
            <PrivateRoute roles={["user"]}>
              <ChildUsersPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/edit-user/:id" // Cần thêm dấu "/" trước ":id"
          element={
            <PrivateRoute roles={["user"]}>
              <UpdateUser />
            </PrivateRoute>
          }
        />

        <Route
          path="/excel-data"
          element={
            <PrivateRoute roles={["admin"]}>
              <ExcelDataPage />
            </PrivateRoute>
          }
        />
        <Route path="/trang-chu" element={<Home />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/news" element={<News />} />
        <Route path="/news/:id" element={<NewsDetail />} />
        <Route path="/market-news" element={<MarketNews />} />
        <Route path="/knowledge-news" element={<KnowledgeNews />} />
        <Route path="/source-news" element={<SourceNews />} />
        <Route path="/instruction" element={<Instruction />} />
        <Route path="/tra-cuu-don-hang" element={<OrderSearch />} />
        <Route path="/dang-ki" element={<RegistrationPage />} />
        <Route path="/dang-nhap" element={<LoginPage />} />
        <Route path="/dich-vu" element={<ServicesPage />} />

        <Route
          path="/add-order-user"
          element={
            <PrivateRoute roles={["user"]}>
              <ChildFeatureRoute feature={CHILD_FEATURES.ORDERS}>
                <AddOrderUser />
              </ChildFeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin"
          element={<Navigate to="/quan-li-khach-hang" replace />}
        />
        <Route
          path="/quan-li-doi-soat"
          element={
            <PrivateRoute roles={["admin"]}>
              <ReconciliationManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/quan-li-doi-soat-tth"
          element={
            <PrivateRoute roles={["admin"]}>
              <TthReconciliationManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/doi-soat-da-doi-soat"
          element={
            <PrivateRoute roles={["admin"]}>
              <NormalInvoices />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/posts/createOrUpdate/:id"
          element={<AddOrUpdatePost />}
        />
        <Route
          path="/admin/news-list"
          element={
            <PrivateRoute roles={["admin"]}>
              <NewsList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/news/:id"
          element={
            <PrivateRoute roles={["admin"]}>
              <NewsEditor />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/chat-inbox"
          element={
            <PrivateRoute roles={["admin"]}>
              <AdminChatInbox />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/omnichannel"
          element={
            <PrivateRoute roles={["admin"]}>
              <AdminOmnichannel />
            </PrivateRoute>
          }
        />
        <Route
          path="/ho-so-cua-toi"
          element={
            <PrivateRoute roles={["admin"]}>
              <AdminMyProfile />
            </PrivateRoute>
          }
        />
        <Route
          path="/cai-dat-he-thong"
          element={
            <PrivateRoute roles={["admin"]}>
              <AdminSystemSettings />
            </PrivateRoute>
          }
        />
        <Route
          path="/permission"
          element={
            <PrivateRoute roles={["admin"]}>
              <EnterprisePermissionPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin-welcome"
          element={
            <PrivateRoute roles={["admin", "member"]}>
              <AdminWelcome />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin-users"
          element={
            <PrivateRoute roles={["admin"]}>
              <UsersAdminPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/order"
          element={
            <PrivateRoute roles={["user"]}>
              <ChildFeatureRoute feature={CHILD_FEATURES.ORDERS}>
                <OrderTable />
              </ChildFeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/don-tth"
          element={
            <PrivateRoute roles={["user"]}>
              <ChildFeatureRoute feature={CHILD_FEATURES.TTH_VIEW}>
                <TthOrderTable />
              </ChildFeatureRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/quan-li-model"
          element={
            <PrivateRoute roles={["admin"]}>
              <Order />
            </PrivateRoute>
          }
        />

        <Route
          path="/quan-li-don-hang/*"
          element={
            <PrivateRoute roles={["admin"]}>
              <OrderAdmin />
            </PrivateRoute>
          }
        />

        <Route
          path="/quan-li-don-tth"
          element={
            <PrivateRoute roles={["admin"]}>
              <TthOrderAdmin />
            </PrivateRoute>
          }
        />

        <Route
          path="/model-details"
          element={
            <PrivateRoute roles={["admin"]}>
              <ModelDetailComponent />
            </PrivateRoute>
          }
        />

        <Route
          path="/add-order"
          element={
            <PrivateRoute roles={["admin"]}>
              <AddOrder />
            </PrivateRoute>
          }
        />
        <Route
          path="/edit-order/:id"
          element={
            <PrivateRoute roles={["admin"]}>
              <EditOrder />
            </PrivateRoute>
          }
        />
        <Route
          path="/quan-li-bai-dang"
          element={
            <PrivateRoute roles={["admin"]}>
              <PostsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/edit-order-user/:id"
          element={
            <PrivateRoute roles={["user"]}>
              <ChildFeatureRoute feature={CHILD_FEATURES.ORDERS}>
                <EditOrderUser />
              </ChildFeatureRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/quan-li-banner"
          element={
            <PrivateRoute roles={["admin"]}>
              <BannerManagement />
            </PrivateRoute>
          }
        />

        <Route
          path="/quan-li-bai-viet"
          element={
            <PrivateRoute roles={["admin"]}>
              <PostsPage />
            </PrivateRoute>
          }
        />
        <Route
          path={CUSTOMER_LIST_PATH}
          element={
            <PrivateRoute roles={["admin"]}>
              <CustomerManagementPage />
            </PrivateRoute>
          }
        />
        <Route
          path={CUSTOMER_NEW_PATH}
          element={
            <PrivateRoute roles={["admin"]}>
              <UpdateCustomerCode />
            </PrivateRoute>
          }
        />
        <Route
          path={`${CUSTOMER_LIST_PATH}/:id/them-ma`}
          element={
            <PrivateRoute roles={["admin"]}>
              <AddCustomerCode />
            </PrivateRoute>
          }
        />
        <Route
          path={`${CUSTOMER_LIST_PATH}/:id`}
          element={
            <PrivateRoute roles={["admin"]}>
              <UpdateCustomerCode />
            </PrivateRoute>
          }
        />
        <Route
          path="/quan-li-khach-hang/update/:id"
          element={
            <PrivateRoute roles={["admin"]}>
              <LegacyCustomerDetailRedirect />
            </PrivateRoute>
          }
        />
        <Route
          path="/quan-li-khach-hang/add/:id"
          element={
            <PrivateRoute roles={["admin"]}>
              <LegacyCustomerAddCodeRedirect />
            </PrivateRoute>
          }
        />
        <Route path="/lien-he" element={<ContactForm />} />
        <Route
          path="/contact-exploration"
          element={
            <PrivateRoute roles={["admin"]}>
              <ContactExploration />
            </PrivateRoute>
          }
        />
        <Route
          path="/control-panel"
          element={
            <PrivateRoute roles={["admin"]}>
              <ControlPanel />
            </PrivateRoute>
          }
        />
        <Route path="/chinh-sach" element={<Policy />} />
        <Route path="/chinh-sach/:slug" element={<PolicyDetail />} />

        <Route
          path="/danh-sach-doi-soat"
          element={
            <PrivateRoute roles={["admin"]}>
              <Invoices />
            </PrivateRoute>
          }
        />
        <Route
          path="/doi-soat-don-hang"
          element={
            <PrivateRoute roles={["admin"]}>
              <InvoiceManagement />
            </PrivateRoute>
          }
        />

        <Route
          path="/don-hang-doi-tac"
          element={
            <PrivateRoute roles={["admin"]}>
              <ContactOrders />
            </PrivateRoute>
          }
        />
        <Route
          path="/hoa-don-doi-tac"
          element={
            <PrivateRoute roles={["admin"]}>
              <ContactInvoices />
            </PrivateRoute>
          }
        />
        <Route
          path="/don-hang-vat-doi-tac"
          element={
            <PrivateRoute roles={["admin"]}>
              <ContactVatOrders />
            </PrivateRoute>
          }
        />
        <Route
          path="/hoa-don-vat-doi-tac"
          element={
            <PrivateRoute roles={["admin"]}>
              <ContactVatInvoices />
            </PrivateRoute>
          }
        />

        <Route
          path="/doi-soat"
          element={
            <PrivateRoute roles={["user"]}>
              <ChildFeatureRoute feature={CHILD_FEATURES.RECONCILE_NORMAL}>
                <UserReconciliation />
              </ChildFeatureRoute>
            </PrivateRoute>
          }
        />
        <Route path="/view-excel" element={<UserExcelPage />} />

        {/* <Route path="/setting-progress" element={<AdminSetting />} /> */}
        <Route
          path="/setting-progress"
          element={
            <PrivateRoute roles={["admin"]}>
              <OrderProcessWarningConfig />
            </PrivateRoute>
          }
        />
        <Route
          path="/setting"
          element={
            <PrivateRoute roles={["admin"]}>
              <ParameterPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/order-setting"
          element={
            <PrivateRoute roles={["admin"]}>
              <OrderParameter />
            </PrivateRoute>
          }
        />
        <Route
          path="/ranking-setting"
          element={
            <PrivateRoute roles={["admin"]}>
              <ParameterUser />
            </PrivateRoute>
          }
        />
        <Route
          path="/databatch"
          element={
            <PrivateRoute roles={["admin"]}>
              <Databatch />
            </PrivateRoute>
          }
        />

        <Route
          path="/about"
          element={
            <PrivateRoute roles={["admin"]}>
              <About />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute roles={["admin"]}>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <PrivateRoute roles={["admin"]}>
              <ListCustomers />
            </PrivateRoute>
          }
        />
        <Route
          path="/contacts"
          element={
            <PrivateRoute roles={["admin"]}>
              <Contacts />
            </PrivateRoute>
          }
        />
        <Route
          path="/contact/:id?"
          element={
            <PrivateRoute roles={["admin"]}>
              <Contact />
            </PrivateRoute>
          }
        />
        <Route
          path="/levels"
          element={<Navigate to={`${CUSTOMER_LIST_PATH}?tab=bang-gia`} replace />}
        />
        <Route
          path="/level/:id?"
          element={
            <PrivateRoute roles={["admin"]}>
              <Level />
            </PrivateRoute>
          }
        />
        <Route
          path="/products"
          element={
            <PrivateRoute roles={["admin"]}>
              <Products />
            </PrivateRoute>
          }
        />
        <Route
          path="/categories"
          element={
            <PrivateRoute roles={["admin"]}>
              <Categories />
            </PrivateRoute>
          }
        />

        <Route
          path="/user-products"
          element={
            <PrivateRoute roles={["user"]}>
              <ChildFeatureRoute feature={CHILD_FEATURES.PRODUCTS}>
                <UserProducts />
              </ChildFeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/doi-soat-vat"
          element={
            <PrivateRoute roles={["user"]}>
              <ChildFeatureRoute feature={CHILD_FEATURES.RECONCILE_VAT}>
                <UserInvoices />
              </ChildFeatureRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/providers"
          element={
            <PrivateRoute roles={["admin"]}>
              <Providers />
            </PrivateRoute>
          }
        />
        <Route
          path="/provider/:id?"
          element={
            <PrivateRoute roles={["admin"]}>
              <Provider />
            </PrivateRoute>
          }
        />
        <Route
          path="/joblogs"
          element={
            <PrivateRoute roles={["admin"]}>
              <JobLogs />
            </PrivateRoute>
          }
        />

        {/* Accounting Module Routes */}
        <Route path="/ke-toan/dashboard" element={<Navigate to="/admin-welcome" replace />} />
        <Route path="/ke-toan/bao-cao" element={<PrivateRoute roles={["admin"]}><AccountingFeatureGate feature={["KẾ TOÁN / Báo cáo", "KẾ TOÁN / Dòng tiền", "KẾ TOÁN / Báo cáo / Doanh thu", "KẾ TOÁN / Báo cáo / Chi phí", "KẾ TOÁN / Báo cáo / Lợi nhuận", "KẾ TOÁN / Báo cáo / Tài sản", "KẾ TOÁN / Báo cáo / Dòng tiền"]}><ReportManagement /></AccountingFeatureGate></PrivateRoute>} />
        <Route path="/ke-toan/doanh-thu" element={<Navigate to="/ke-toan/bao-cao?tab=revenue" replace />} />
        <Route path="/ke-toan/chi-phi" element={<Navigate to="/ke-toan/bao-cao?tab=expense" replace />} />
        <Route path="/ke-toan/cong-no" element={<PrivateRoute roles={["admin"]}><AccountingFeatureGate feature={["KẾ TOÁN / Công nợ", "KẾ TOÁN / Công nợ / Phải thu", "KẾ TOÁN / Công nợ / Phải trả"]}><DebtManagement /></AccountingFeatureGate></PrivateRoute>} />
        <Route path="/ke-toan/phieu-thu-chi" element={<PrivateRoute roles={["admin"]}><AccountingFeatureGate feature={["KẾ TOÁN / Phiếu thu/chi", "KẾ TOÁN / Phiếu thu/chi / Phiếu thu", "KẾ TOÁN / Phiếu thu/chi / Phiếu chi"]}><VoucherManagement /></AccountingFeatureGate></PrivateRoute>} />
        <Route path="/ke-toan/cau-hinh" element={<PrivateRoute roles={["admin"]}><AccountingFeatureGate feature={["KẾ TOÁN / Cấu hình", "KẾ TOÁN / Cấu hình / Tài khoản ngân hàng", "KẾ TOÁN / Cấu hình / Danh mục thu", "KẾ TOÁN / Cấu hình / Danh mục chi", "KẾ TOÁN / Cấu hình / Đối tượng chi", "KẾ TOÁN / Cấu hình / Đối tượng thu", "KẾ TOÁN / Cấu hình / Cấu hình lương"]}><AccountingConfig /></AccountingFeatureGate></PrivateRoute>} />
        <Route path="/ke-toan/nhan-su" element={<PrivateRoute roles={["admin"]}><AccountingFeatureGate feature={["KẾ TOÁN / Lương & Tạm ứng", "KẾ TOÁN / Sổ chi", "KẾ TOÁN / Sổ chi / Bảng lương", "KẾ TOÁN / Sổ chi / Tạm ứng", "KẾ TOÁN / Sổ chi / Chi phí trả trước"]}><HrAccounting /></AccountingFeatureGate></PrivateRoute>} />
        <Route path="/ke-toan/tai-khoan-ngan-hang" element={<Navigate to="/ke-toan/cau-hinh?tab=bank" replace />} />
        <Route path="/ke-toan/dong-tien" element={<Navigate to="/ke-toan/bao-cao?tab=cashflow" replace />} />

        {/* Kho Vận Module Routes */}
        <Route path="/kho-van/quet-nhap" element={<PrivateRoute roles={["admin"]}><AccountingFeatureGate feature={WAREHOUSE_SCAN_IN_FEATURE} fetchPermissions={fetchWarehousePermissions}><ScanInPage /></AccountingFeatureGate></PrivateRoute>} />
        <Route path="/kho-van/quet-xuat" element={<PrivateRoute roles={["admin"]}><AccountingFeatureGate feature={WAREHOUSE_SCAN_OUT_FEATURE} fetchPermissions={fetchWarehousePermissions}><ScanOutPage /></AccountingFeatureGate></PrivateRoute>} />
        <Route path="/kho-van/don-vo-chu" element={<PrivateRoute roles={["admin"]}><AccountingFeatureGate feature={WAREHOUSE_UNCLAIMED_FEATURE} fetchPermissions={fetchWarehousePermissions}><UnknownCodesPage /></AccountingFeatureGate></PrivateRoute>} />

        {/* Accounting route redirects */}
        <Route path="/ke-toan/ceo-dashboard" element={<Navigate to="/admin-welcome" replace />} />
        <Route path="/ke-toan/cong-no-phai-thu" element={<Navigate to="/ke-toan/cong-no?tab=receivable" replace />} />
        <Route path="/ke-toan/cong-no-phai-tra" element={<Navigate to="/ke-toan/cong-no?tab=payable" replace />} />
        <Route path="/ke-toan/phieu-thu" element={<Navigate to="/ke-toan/phieu-thu-chi?tab=receipt" replace />} />
        <Route path="/ke-toan/phieu-chi" element={<Navigate to="/ke-toan/phieu-thu-chi?tab=payment" replace />} />
        <Route path="/ke-toan/danh-muc-chi-phi" element={<Navigate to="/ke-toan/cau-hinh?tab=expense-cat" replace />} />
        <Route path="/ke-toan/luong" element={<Navigate to="/ke-toan/nhan-su?tab=salary" replace />} />
        <Route path="/ke-toan/tam-ung" element={<Navigate to="/ke-toan/nhan-su?tab=advance" replace />} />
        <Route path="/ke-toan/chi-phi-tra-truoc" element={<Navigate to="/ke-toan/nhan-su?tab=prepaid" replace />} />
      </Routes>

      <ToastContainer position="top-right" autoClose={3000} />
      {!isStaffUser && <FloatingContactWidget />}
      {!isStaffUser && <TradeWebChatClient />}
    </Router>
  );
};

export default App;
